<template>
  <div>
    <!-- Video Modal -->
    <b-modal
      v-model="isVideoModalVisible"
      content-class="airspirit-modal"
      hide-footer
      hide-header
      header-class="airspirit-modal-header"
      body-class="newsletter-subscriber-modal-body pl-4 pr-4"
      size="lg"
      @hidden="closeVideoModal"
    >
      <div 
        style="width: 100%; height: 700px;"
        v-if="content"
      >
        <iframe 
          :src="`https://player.vimeo.com/video/${content['vimeo_id']}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`" 
          frameborder="0" 
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" 
          style="position:absolute;top:0;left:0;width:100%;height:100%;" 
        />
      </div>
    </b-modal>

    <!-- Accident Audio Modal -->
    <b-modal
      v-model="isAccidentModalVisible"
      content-class="airspirit-modal"
      hide-footer
      hide-header
      header-class="airspirit-modal-header"
      body-class="newsletter-subscriber-modal-body pl-4 pr-4"
      size="lg"
      @hidden="closeAccidentModal"
    >
      <div 
        @click="isAccidentModalVisible = false"
        class="airspirit-modal-header--close-icon"
        style="float: right; cursor: pointer;"
      >
        <img 
          :src="require('@/assets/images/pictos/croix.svg')"
          :alt="$t('content_modal.close_icon_alt')"
        />
      </div>

      <div v-if="content" class="mt-4">
        <h4>
          {{ content.aircraft_type }}
          <template v-if="content.location && content.location !== 'Unknown'">
            | {{ content.location }}
          </template>
        </h4>
        <p class="text-muted">
          {{ content.reporting_organization }} - 
          {{ content.report_date ? $t('content_modal.published_date', { date: $dayjs(content.report_date).format('DD/MM/YYYY') }) : $t('content_modal.published_date_na') }}
        </p>

        <!-- Synthesis section - hidden by default, shown when View Summary is clicked -->
        <div v-if="content.synthesis && showSynthesis" class="mt-3 mb-4 synthesis-section">
          <h5>{{ $t('content_modal.synthesis_title') }}</h5>
          <p style="white-space: pre-wrap;">{{ content.synthesis }}</p>
        </div>

        <div v-if="hasAnyAudio(content)" class="mt-4">
          <h5>
            {{ $t('content_modal.audio_report_title', { language: getCurrentAudioLanguageName(content) }) }}
          </h5>
          <p class="text-muted" style="font-size: 0.9em;">
            {{ $t('content_modal.audio_language_hint') }}
          </p>
          
          <!-- Audio player with user's selected language -->
          <audio controls style="width: 100%;" class="mt-2" :key="getAudioUrl(content)">
            <source :src="getAudioUrl(content)" type="audio/mpeg">
            {{ $t('content_modal.audio_not_supported') }}
          </audio>
        </div>

        <div v-if="!hasAnyAudio(content)" class="mt-4">
          <p class="text-muted">{{ $t('content_modal.audio_not_available') }}</p>
        </div>

        <!-- Action buttons -->
        <div class="mt-4 accident-modal-buttons">
          <button 
            v-if="content.synthesis"
            @click="toggleSynthesis"
            class="btn btn-outline-primary accident-modal-button"
          >
            {{ showSynthesis ? $t('content_modal.hide_summary') : $t('content_modal.view_summary') }}
          </button>
          <a 
            v-if="content.report_url" 
            :href="content.report_url" 
            target="_blank" 
            class="btn btn-primary accident-modal-button"
          >
            {{ $t('content_modal.view_full_report') }}
          </a>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'ContentModal',
  props: {
    content: {
      type: Object,
      default: null
    },
    isVideoModal: {
      type: Boolean,
      default: false
    },
    isAccidentModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showSynthesis: false
    }
  },
  computed: {
    isVideoModalVisible: {
      get() {
        return this.isVideoModal
      },
      set(value) {
        this.$emit('update:isVideoModal', value)
      }
    },
    isAccidentModalVisible: {
      get() {
        return this.isAccidentModal
      },
      set(value) {
        this.$emit('update:isAccidentModal', value)
      }
    }
  },
  watch: {
    isAccidentModal(newVal) {
      if (newVal) {
        this.showSynthesis = false
      }
    }
  },
  methods: {
    closeVideoModal() {
      this.$emit('update:isVideoModal', false)
    },
    closeAccidentModal() {
      this.$emit('update:isAccidentModal', false)
      this.showSynthesis = false
    },
    getCurrentAudioLanguageName(content) {
      const userLangAudio = this.getCurrentLanguageAudio(content)
      if (userLangAudio) {
        return this.getLanguageName(this.$i18n.locale)
      }
      
      const fallbackLang = this.getFirstAvailableLanguage(content)
      return this.getLanguageName(fallbackLang)
    },
    getFirstAvailableLanguage(content) {
      if (content.audio_en) return 'en'
      if (content.audio_fr) return 'fr'
      if (content.audio_es) return 'es'
      if (content.audio_de) return 'de'
      return 'en'
    },
    getCurrentLanguageAudio(content) {
      const audioMap = {
        'en': content.audio_en,
        'fr': content.audio_fr,
        'es': content.audio_es,
        'de': content.audio_de
      }
      return audioMap[this.$i18n.locale]
    },
    getLanguageName(langCode) {
      const names = {
        'en': 'English',
        'fr': 'Français',
        'es': 'Español',
        'de': 'Deutsch'
      }
      return names[langCode] || langCode
    },
    hasAnyAudio(content) {
      return content.audio_en || 
             content.audio_fr || 
             content.audio_es || 
             content.audio_de ||
             content.audio
    },
    getAudioUrl(content) {
      const userLangAudio = this.getCurrentLanguageAudio(content)
      if (userLangAudio) {
        return userLangAudio
      }
      
      const fallbackLang = this.getFirstAvailableLanguage(content)
      const audioMap = {
        'en': content.audio_en,
        'fr': content.audio_fr,
        'es': content.audio_es,
        'de': content.audio_de
      }
      return audioMap[fallbackLang] || content.audio || ''
    },
    toggleSynthesis() {
      this.showSynthesis = !this.showSynthesis
      
      if (this.showSynthesis) {
        this.$nextTick(() => {
          const synthesisElement = document.querySelector('.modal-body .synthesis-section')
          if (synthesisElement) {
            synthesisElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.accident-modal-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: stretch;
}

@media (min-width: 768px) {
  .accident-modal-buttons {
    flex-direction: row;
    justify-content: center;
    gap: 15px;
  }
}

.accident-modal-button {
  width: 100%;
}

@media (min-width: 768px) {
  .accident-modal-button {
    width: auto;
    min-width: 180px;
  }
}
</style>
